import { Component, computed, inject } from '@angular/core';
import { InvoiceService } from '@services/invoice.service';
import { CompanyService } from '@services/company.service';
import { OAuthService } from 'angular-oauth2-oidc';
import { GS1LayoutModule, GS1SpinnerDirective, GS1LoadingService, NavItem, SideMenuSlot } from '@gs1france/gs1-components-library';
import { TranslateService } from '@ngx-translate/core';
import { map, take } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { SideMenuCertificateComponent } from './side-menu-certificate/side-menu-certificate.component';
import { StorageService } from '@services/storage.service';

@Component({
  selector: 'app-side-menu',
  templateUrl: './side-menu.component.html',
  imports: [GS1LayoutModule, GS1SpinnerDirective, SideMenuCertificateComponent]
})
export class SideMenuComponent {
  oauthService = inject(OAuthService);
  translateService = inject(TranslateService);

  loadingService = inject(GS1LoadingService);

  companyService = inject(CompanyService);
  invoiceService = inject(InvoiceService);
  storageService = inject(StorageService);

  company = computed<string>(() => this.companyService.company()?.name);

  footerSlot: SideMenuSlot = {
    icon: 'bi-file-earmark-arrow-down',
    ariaLabel: this.translateService.instant('nav.certificate.ariaLabel')
  };

  menuWidth: string = getComputedStyle(document.documentElement).getPropertyValue('--side-menu-width');

  invoicesToPay = toSignal<number>(this.invoiceService.getAllInvoices(0, 10).pipe(
    take(1),
    map((invoices) => {
      if (invoices && invoices.data.length) return invoices.data.filter((invoice) => invoice.leftToPay > 0).length;
      return 0;
    })
  ));

  menuItems: NavItem[] = [
      {
        label: this.translateService.instant('nav.home'),
        icon: 'bi-houses',
        route: '/accueil'
      },
      {
        label: this.translateService.instant('nav.dashboard'),
        icon: 'bi-house',
        route: '/tableau-de-bord',
        badge: this.invoicesToPay
      },
      {
        label: this.translateService.instant('nav.administration.label'),
        icon: 'bi-buildings',
        open: false,
        children: [
          {
            label: this.translateService.instant('nav.administration.profile'),
            route: '/profil'
          },
          {
            label: this.translateService.instant('nav.administration.company'),
            route: '/entreprise'
          },
          {
            label: this.translateService.instant('nav.administration.invoices'),
            route: '/factures'
          },
          {
            label: this.translateService.instant('nav.administration.documents'),
            route: '/documents'
          },
          {
            label: this.translateService.instant('nav.administration.users'),
            route: '/utilisateurs'
          }
        ]
      },
      {
        label: this.translateService.instant('nav.collaboration.label'),
        icon: 'bi-chat',
        open: false,
        children: [
          {
            label: this.translateService.instant('nav.collaboration.summary'),
            route: '/collaboration'
          },
          {
            label: this.translateService.instant('nav.collaboration.workgroups'),
            route: '/groupes-de-travail'
          },
          {
            label: this.translateService.instant('nav.collaboration.communities'),
            url: 'https://www.gs1.fr/collaborer-gs1-france/communautes-dinteret-gs1'
          },
          {
            label: this.translateService.instant('nav.collaboration.evolutions'),
            route: '/standard'
          }
        ]
      },
      {
        label: this.translateService.instant('nav.codes.label'),
        icon: 'bi-upc-scan',
        open: false,
        children: [
          {
            label: this.translateService.instant('nav.codes.prefixes'),
            route: 'codes/prefixes'
          },
          {
            label: this.translateService.instant('nav.codes.gtin'),
            url: 'https://codeonline-gtin.gs1.fr/'
          },
          {
            label: this.translateService.instant('nav.codes.gln'),
            url: 'https://codeonline-gln.gs1.fr/'
          },
          {
            label: this.translateService.instant('nav.codes.other-codes'),
            route: 'codes/autres'
          }
        ]
      }
    ];

  changeCompany() {
    this.loadingService.startLoading('company-change');
    this.companyService.clearCompany();
    this.oauthService.initLoginFlow('', { companies: true, prompt: 'login' });
  }
}
